<script setup lang="ts">
import { computed, nextTick, ref, useId, watch } from 'vue'
import GbButton from '@/components/GbButton.vue'
import SelectionChip from '@/components/SelectionChip.vue'
import type { StudentCharacteristic } from './sdi_types'

const props = withDefaults(defineProps<{
  characteristicOptions?: StudentCharacteristic[]
}>(), {
  characteristicOptions: () => [],
})

const selectedCharacteristics = defineModel<StudentCharacteristic[]>({ default: () => [] })

const emit = defineEmits<{
  submit: []
  clearSelections: []
}>()

const selectionMessageId = useId()
const optionsFieldset = ref<HTMLFieldSetElement | null>(null)
const hasTriedSubmittingWithoutSelection = ref(false)

const hasSelection = computed(() => selectedCharacteristics.value.length > 0)
const isShowingSelectionMessage = computed(() => !hasSelection.value && hasTriedSubmittingWithoutSelection.value)
const optionsDescribedBy = computed(() => (isShowingSelectionMessage.value ? selectionMessageId : undefined))

async function focusFirstOption() {
  await nextTick()
  optionsFieldset.value?.querySelector<HTMLInputElement>('input')?.focus()
}

function handleSubmitAttempt() {
  if (!hasSelection.value) {
    hasTriedSubmittingWithoutSelection.value = true
    focusFirstOption()
    return
  }
  emit('submit')
}

watch(hasSelection, (isNowSelected) => {
  if (isNowSelected) {
    hasTriedSubmittingWithoutSelection.value = false
  }
})
</script>

<template>
  <form @submit.prevent="handleSubmitAttempt">
    <fieldset
      ref="optionsFieldset"
      class="mb-7"
      :aria-describedby="optionsDescribedBy"
    >
      <legend class="sr-only">Characteristic Options (select at least one)</legend>
      <SelectionChip
        v-model="selectedCharacteristics"
        :items="characteristicOptions"
        item-key="id"
        item-label="name"
        gb-theme="blue"
      />
    </fieldset>
    <div class="my-2 flex items-center text-md">
      <GbButton
        type="submit"
        gb-theme="aiPrimary"
        class="mr-6"
        :class="{ 'cursor-not-allowed': !hasSelection }"
        data-testid="generate-sdi-strategies"
      >
        Generate Strategies
      </GbButton>
      <button
        v-if="hasSelection"
        type="button"
        class="text-blue-400 hover:underline"
        @click="emit('clearSelections')"
      >
        Clear selections
      </button>
      <span v-else-if="isShowingSelectionMessage" :id="selectionMessageId" role="alert">
        Please make at least one selection above.
      </span>
    </div>
  </form>
</template>
